import { createFileRoute } from "@tanstack/react-router";
import useTranslation from "../../../hooks/useTranslation";
import { useGeneratedTextCreateSpeech } from "../../../hooks/useGeneratedTextUpdateSpeech";
import { useGeneratedTextDetails } from "../../../hooks/useGeneratedTextDetails";
import { useDailyTextStartGeneration } from "../../../hooks/useDailyTextStartGeneration";
import { useIncrementGeneratedTextViewCounter } from "../../../hooks/useIncrementGeneratedTextViewCounter";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  usePaginatedVocabularyList,
  VocabularyDto,
} from "../../../hooks/usePaginatedVocabularyList";
import { useGeneratedTextInlineContinuation } from "../../../hooks/useGeneratedTextInlineContinuation";
import { resetStreamingTextIntegrator } from "../../../hooks/useStreamingTextIntegrator";
import { GeneratedTextDetailsTextPane } from "../../../components/GeneratedTextDetailsTextPane";
import { CreateVocabularyTextInlinePreview } from "../../../components/CreateVocabularyTextInlinePreview";
import { LanguageHelperPanel } from "../../../components/LanguageHelperPanel";
import { useChapterRetrieveQuery } from "../../../hooks/useChapterRetrieveQuery";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import { WordCard } from "../../../components/WordCard/WordCard";
import { resolveGeneratedTextContent } from "../../../utils/generatedTextContent";
import { resolveActiveTextHighlightWords } from "../../../utils/resolveActiveTextHighlightWords";
import { resolveSelectedVocabularyWordsForChapterGeneration } from "../../../utils/resolveSelectedVocabularyWordsForChapterGeneration";
import { resolveWordAddedToastKey } from "../../../utils/resolveWordAddedToastKey";
import { shouldRefetchVocabularyAfterCompletion } from "../../../utils/shouldRefetchVocabularyAfterCompletion";
import {
  resolveNextPlaybackRate,
  resolveSeekTime,
} from "../../../utils/readingAudioPlayback";
import {
  pruneVocabularySelectionToExistingIds,
  resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText,
} from "../../../utils/vocabularySelectionState";
import { resetAppScrollPosition } from "../../../utils/resetAppScrollPosition";
import { notifySnackbar } from "../../../lib/notifySnackbar";
import {
  ChevronRightIcon,
  LightBulbIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { ImageImportMetadata } from "../../../hooks/types/GeneratedTexts";

export function mergeOptimisticPhraseVocabularies(
  current: VocabularyDto[],
  incoming: VocabularyDto | undefined,
  supersededIds: number[],
) {
  const removedIds = new Set(supersededIds);
  const next = current.filter(
    (entry) => !removedIds.has(entry.id) && (!incoming || entry.id !== incoming.id),
  );
  if (incoming) {
    next.push(incoming);
  }
  return next;
}

function getVocabularyPrimaryUsage(vocabulary: VocabularyDto) {
  return [...vocabulary.usages].sort((left, right) => {
    if (left.text !== right.text) return left.text - right.text;
    if ((left.chapter_id ?? -1) !== (right.chapter_id ?? -1)) {
      return (left.chapter_id ?? -1) - (right.chapter_id ?? -1);
    }
    if (left.start_idx !== right.start_idx) return left.start_idx - right.start_idx;
    return left.end_idx - right.end_idx;
  })[0];
}

function isUsageFullyCoveredByAnotherUsage(
  usage: VocabularyDto["usages"][number],
  otherUsage: VocabularyDto["usages"][number],
) {
  return (
    usage.text === otherUsage.text &&
    (usage.chapter_id ?? null) === (otherUsage.chapter_id ?? null) &&
    otherUsage.start_idx <= usage.start_idx &&
    otherUsage.end_idx >= usage.end_idx &&
    (otherUsage.start_idx < usage.start_idx || otherUsage.end_idx > usage.end_idx)
  );
}

function isVocabularySubsumedByAnother(
  vocabulary: VocabularyDto,
  others: VocabularyDto[],
) {
  const primaryUsage = getVocabularyPrimaryUsage(vocabulary);
  if (!primaryUsage) return false;
  return others.some((other) => {
    if (other.id === vocabulary.id || other.usages.length === 0) return false;
    return other.usages.some((otherUsage) =>
      isUsageFullyCoveredByAnotherUsage(primaryUsage, otherUsage),
    );
  });
}

function compareVocabularyCanonicalPriority(left: VocabularyDto, right: VocabularyDto) {
  const leftUsage = getVocabularyPrimaryUsage(left);
  const rightUsage = getVocabularyPrimaryUsage(right);
  if (!leftUsage && !rightUsage) return left.id - right.id;
  if (!leftUsage) return 1;
  if (!rightUsage) return -1;
  if (leftUsage.text !== rightUsage.text) return leftUsage.text - rightUsage.text;
  if ((leftUsage.chapter_id ?? -1) !== (rightUsage.chapter_id ?? -1)) {
    return (leftUsage.chapter_id ?? -1) - (rightUsage.chapter_id ?? -1);
  }
  const leftSpan = leftUsage.end_idx - leftUsage.start_idx;
  const rightSpan = rightUsage.end_idx - rightUsage.start_idx;
  if (leftSpan !== rightSpan) return rightSpan - leftSpan;
  if (leftUsage.start_idx !== rightUsage.start_idx) {
    return leftUsage.start_idx - rightUsage.start_idx;
  }
  if (leftUsage.end_idx !== rightUsage.end_idx) {
    return leftUsage.end_idx - rightUsage.end_idx;
  }
  return right.id - left.id;
}

export function resolveCanonicalPhraseVocabularyCards(vocabularies: VocabularyDto[]) {
  const ordered = [...vocabularies].sort(compareVocabularyCanonicalPriority);
  const seenKeys = new Set<string>();
  return ordered.filter((vocabulary) => {
    if (vocabulary.usages.length === 0) return true;
    const primaryUsage = getVocabularyPrimaryUsage(vocabulary);
    const key = primaryUsage
      ? `${primaryUsage.text}:${primaryUsage.chapter_id ?? "root"}:${primaryUsage.start_idx}:${primaryUsage.end_idx}`
      : `voc:${vocabulary.id}`;
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return !isVocabularySubsumedByAnother(vocabulary, ordered);
  });
}

export const Route = createFileRoute("/app/reeprise/$generatedTextId")({
  component: GeneratedTextDetailsPage,
  validateSearch: (search) => {
    const parsedFocusToken = Number(search.focusToken);
    return {
      chapter:
        search.chapter === undefined ? undefined : Number(search.chapter),
      focusWord:
        typeof search.focusWord === "string" ? search.focusWord : undefined,
      focusSentence:
        typeof search.focusSentence === "string"
          ? search.focusSentence
          : undefined,
      focusToken: Number.isFinite(parsedFocusToken)
        ? parsedFocusToken
        : undefined,
      jumpTo:
        search.jumpTo === "vocabulary"
          ? "vocabulary"
          : undefined,
    };
  },
});

function resolveImageImportStageLabelKey(
  processingStage: ImageImportMetadata["processing_stage"],
  mode: ImageImportMetadata["mode"],
) {
  if (processingStage === "FETCHING_URL") {
    return "article_import_loading_reading";
  }
  if (processingStage === "EXTRACTING_ARTICLE") {
    return "article_import_loading_reading";
  }
  if (processingStage === "READING_IMAGE") {
    return "image_import_processing_reading_image";
  }
  if (processingStage === "EXTRACTING_TEXT") {
    return "image_import_processing_extracting_text";
  }
  if (processingStage === "CREATING_CARD") {
    return "image_import_processing_creating_card";
  }
  if (processingStage === "ADAPTING_TEXT") {
    return mode === "adapt"
      ? "image_import_processing_adapting_text"
      : "image_import_processing_creating_card";
  }
  return "image_import_processing_reading_image";
}

function resolveImageImportFailureMessage(
  metadata: ImageImportMetadata | null | undefined,
  fallbackMessage: string,
) {
  if (metadata?.source === "url") {
    if (fallbackMessage.trim()) {
      return fallbackMessage.trim();
    }
    return "Something went wrong while adapting the article.";
  }
  const errorCode = metadata?.processing_error_code ?? "";
  if (errorCode === "UNSUPPORTED_SOURCE") {
    return "This image doesn't contain a continuous reading text. Select extract vocabulary after the image upload.";
  }
  if (errorCode === "ARTICLE_SELECTION_INCOHERENT") {
    return "We couldn't reliably determine one article from the selected area.";
  }
  if (errorCode === "LOW_READING_ORDER_CONFIDENCE") {
    return "We couldn't reliably determine the main text from this image.";
  }
  if (errorCode === "LOW_OCR_CONFIDENCE") {
    return "This image is too difficult to read accurately. Try a clearer photo.";
  }
  if (errorCode === "EMPTY_TEXT") {
    return "We could not read enough text from this image. Please try again with a clearer photo.";
  }
  return fallbackMessage.trim() || "We couldn't create a reading card from this image.";
}

function GeneratedTextDetailsPage() {
  const { translate } = useTranslation();
  const { generatedTextId } = Route.useParams();
  const { chapter, focusWord, focusSentence, focusToken, jumpTo } = Route.useSearch();
  const { createSpeechMutation } = useGeneratedTextCreateSpeech();
  const reepriseDetails = useGeneratedTextDetails(Number(generatedTextId));
  const startDailyTextGeneration = useDailyTextStartGeneration();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const chapterDetails = useChapterRetrieveQuery({
    chapterCounter: chapter,
    generatedTextId: Number(generatedTextId),
  });
  const incrementCounter = useIncrementGeneratedTextViewCounter({
    textId: Number(generatedTextId),
  });
  const isDailyOrRabbitHole =
    reepriseDetails.data?.origin === "DAILY_NUDGE" ||
    reepriseDetails.data?.origin === "RABBIT_HOLE";
  const isManualDeferred =
    reepriseDetails.data?.origin === "MANUAL" &&
    (reepriseDetails.data?.image_import_metadata?.source === "image" ||
      reepriseDetails.data?.image_import_metadata?.source === "url");
  const restartableFailedDeferredText =
    reepriseDetails.data?.status === "FAILED" &&
    (isDailyOrRabbitHole ||
      (isManualDeferred && !reepriseDetails.data.chatgpt_output?.trim()));

  const topRef = useRef<HTMLDivElement | null>(null);
  const vocabularySectionRef = useRef<HTMLDivElement | null>(null);
  const startGenerationInflightKeyRef = useRef<string | null>(null);
  const startGenerationSucceededKeyRef = useRef<string | null>(null);

  const [selectedVocabularyIds, setSelectedVocabularyIds] = useState<number[]>(
    [],
  );
  const [deselectedVocabularyIds, setDeselectedVocabularyIds] = useState<
    number[]
  >([]);
  const [recentVocabulary, setRecentVocabulary] = useState<
    { word: string; translation?: string | null }[]
  >([]);
  const [optimisticSelectedWordsById, setOptimisticSelectedWordsById] = useState<
    Record<number, string>
  >({});
  const [titleOnlyVocabularyIds, setTitleOnlyVocabularyIds] = useState<number[]>([]);
  const [focusRequest, setFocusRequest] = useState<{
    word: string;
    sentence?: string | null;
    token: number;
  } | null>(null);
  const [isLanguageHelperOpen, setIsLanguageHelperOpen] = useState(false);
  const [showVocabularyColumn, setShowVocabularyColumn] = useState(true);
  const [activeSavedGroups, setActiveSavedGroups] = useState<number[][]>([]);
  const [activeGroupsTextOffset, setActiveGroupsTextOffset] = useState(0);
  const [phraseVocabularyIds, setPhraseVocabularyIds] = useState<Set<number>>(new Set());
  const [optimisticPhraseVocabularies, setOptimisticPhraseVocabularies] = useState<VocabularyDto[]>([]);
  const selectedVocabularyIdsRef = useRef<number[]>([]);
  const deselectedVocabularyIdsRef = useRef<number[]>([]);
  const audioSrc =
    chapter !== undefined
      ? `/streams/audio/${generatedTextId}/?chapter=${chapter}`
      : `/streams/audio/${generatedTextId}/`;

  const onVocabularyCreate = (
    vocabularyDto: VocabularyDto,
    options?: { source: "title" | "text" },
  ) => {
    notifySnackbar({
      message: translate(resolveWordAddedToastKey(vocabularyDto.word)),
      appearance: "vocabulary-added",
    });
    setSelectedVocabularyIds((oldValue) =>
      oldValue.includes(vocabularyDto.id)
        ? oldValue
        : [...oldValue, vocabularyDto.id],
    );
    setDeselectedVocabularyIds((prev) =>
      prev.filter((entry) => entry !== vocabularyDto.id),
    );
    if (options?.source === "title") {
      setTitleOnlyVocabularyIds((prev) =>
        prev.includes(vocabularyDto.id) ? prev : [...prev, vocabularyDto.id],
      );
    }
    if (vocabularyDto.id && vocabularyDto.word?.trim()) {
      setOptimisticSelectedWordsById((prev) => ({
        ...prev,
        [vocabularyDto.id]: vocabularyDto.word.trim(),
      }));
    }
    if (vocabularyDto.word?.trim()) {
      setRecentVocabulary((prev) => [
        { word: vocabularyDto.word, translation: vocabularyDto.translation },
        ...prev.filter((item) => item.word !== vocabularyDto.word),
      ].slice(0, 5));
    }
  };

  useEffect(() => {
    resetAppScrollPosition();
    setSelectedVocabularyIds([]);
    setDeselectedVocabularyIds([]);
    setOptimisticSelectedWordsById({});
    setTitleOnlyVocabularyIds([]);
    setIsLanguageHelperOpen(false);
  }, [generatedTextId, chapter]);

  useEffect(() => {
    selectedVocabularyIdsRef.current = selectedVocabularyIds;
    deselectedVocabularyIdsRef.current = deselectedVocabularyIds;
  }, [deselectedVocabularyIds, selectedVocabularyIds]);

  useEffect(() => {
    const openLanguageHelperOverlay = () => {
      setIsLanguageHelperOpen(true);
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent("focus-language-helper"));
      });
    };
    window.addEventListener(
      "open-language-helper-overlay",
      openLanguageHelperOverlay,
    );
    return () => {
      window.removeEventListener(
        "open-language-helper-overlay",
        openLanguageHelperOverlay,
      );
    };
  }, []);

  useEffect(() => {
    if (!focusWord && !focusSentence) {
      return;
    }
    setFocusRequest({
      word: focusWord ?? "",
      sentence: focusSentence ?? null,
      token: focusToken ?? Date.now(),
    });
  }, [focusSentence, focusToken, focusWord]);

  useEffect(() => {
    if (jumpTo !== "vocabulary") {
      return;
    }
    setShowVocabularyColumn(true);
    window.requestAnimationFrame(() => {
      vocabularySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [jumpTo]);

  useEffect(() => {
    const text = reepriseDetails.data;
    if (
      !text ||
      (text.status !== "PENDING" && !restartableFailedDeferredText) ||
      chapter !== undefined
    ) {
      return;
    }
    const isDeferredImageReadingText =
      text.origin === "MANUAL" && text.image_import_metadata?.source === "image";
    const isDeferredArticleUrlText =
      text.origin === "MANUAL" && text.image_import_metadata?.source === "url";
    const isDeferredDailyText = text.origin === "DAILY_NUDGE";
    const isDeferredRabbitHoleText = text.origin === "RABBIT_HOLE";
    if (!isDeferredDailyText && !isDeferredRabbitHoleText && !isDeferredImageReadingText && !isDeferredArticleUrlText) {
      return;
    }
    const requestKey = `${text.id}:${chapter ?? "root"}`;
    if (
      startGenerationInflightKeyRef.current === requestKey ||
      startGenerationSucceededKeyRef.current === requestKey
    ) {
      return;
    }
    startGenerationInflightKeyRef.current = requestKey;
    void startDailyTextGeneration
      .mutateAsync({
        generatedTextId: Number(generatedTextId),
      })
      .then(() => {
        startGenerationSucceededKeyRef.current = requestKey;
      })
      .catch(() => {
        if (startGenerationInflightKeyRef.current === requestKey) {
          startGenerationInflightKeyRef.current = null;
        }
      })
      .finally(() => {
        if (startGenerationInflightKeyRef.current === requestKey) {
          startGenerationInflightKeyRef.current = null;
        }
      });
  }, [
    chapter,
    generatedTextId,
    restartableFailedDeferredText,
    reepriseDetails.data,
    startDailyTextGeneration,
  ]);

  const inlineContinuationMutation = useGeneratedTextInlineContinuation({
    generatedTextId: Number(generatedTextId),
  });

  const paginatedVocabularyList = usePaginatedVocabularyList({
    generated_text_id: Number(generatedTextId),
  });
  const completionVocabularyRefetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => {
      void incrementCounter.mutateAsync();
    }, 5_000);

    return () => clearTimeout(id);
  }, [generatedTextId, incrementCounter]);

  useEffect(() => {
    const completionRefetchKey = `${generatedTextId}:${chapter ?? "root"}`;
    if (
      !shouldRefetchVocabularyAfterCompletion({
        status: reepriseDetails.data?.status,
        completionRefetchKey,
        lastRefetchedCompletionKey: completionVocabularyRefetchKeyRef.current,
      })
    ) {
      return;
    }
    completionVocabularyRefetchKeyRef.current = completionRefetchKey;
    void paginatedVocabularyList.refetch();
  }, [
    chapter,
    generatedTextId,
    paginatedVocabularyList,
    reepriseDetails.data?.status,
  ]);

  useEffect(() => {
    const handler = (e: Event) => {
      const event = e as CustomEvent<{
        groups: number[][];
        textOffset: number;
      }>;
      setActiveSavedGroups(event.detail?.groups ?? []);
      setActiveGroupsTextOffset(event.detail?.textOffset ?? 0);
    };
    document.addEventListener("vocabulary-groups-changed", handler);
    return () =>
      document.removeEventListener("vocabulary-groups-changed", handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const event = e as CustomEvent<{
        phraseId: number;
        supersededIds: number[];
        vocabulary?: VocabularyDto;
      }>;
      const { phraseId, supersededIds, vocabulary } = event.detail ?? {};
      if (vocabulary || (supersededIds && supersededIds.length > 0)) {
        setOptimisticPhraseVocabularies((prev) =>
          mergeOptimisticPhraseVocabularies(prev, vocabulary, supersededIds ?? []),
        );
      }
      if (phraseId) {
        setPhraseVocabularyIds((prev) => {
          const next = new Set(prev);
          next.add(phraseId);
          for (const sid of supersededIds ?? []) next.delete(sid);
          return next;
        });
      }
    };
    document.addEventListener("vocabulary-phrase-created", handler);
    return () =>
      document.removeEventListener("vocabulary-phrase-created", handler);
  }, []);

  const vocabulariesRaw =
    paginatedVocabularyList.data?.pages.flatMap((page) => page.results) || [];

  const consumedStarts = useMemo(() => {
    const set = new Set<number>();
    for (const group of activeSavedGroups) {
      for (const start of group) set.add(start + activeGroupsTextOffset);
    }
    return set;
  }, [activeSavedGroups, activeGroupsTextOffset]);

  const vocabularies = useMemo(() => {
    const mergedById = new Map<number, VocabularyDto>();
    optimisticPhraseVocabularies.forEach((vocabulary) => {
      if (!vocabulariesRaw.some((v) => v.id === vocabulary.id)) {
        mergedById.set(vocabulary.id, vocabulary);
      }
    });
    vocabulariesRaw.forEach((vocabulary) => {
      mergedById.set(vocabulary.id, vocabulary);
    });
    const merged = resolveCanonicalPhraseVocabularyCards(
      Array.from(mergedById.values()),
    );
    if (consumedStarts.size === 0) return merged;
    return merged.filter((voc) => {
      if (voc.usages.length === 0) return true;
      const allUsagesConsumed = voc.usages.every((u) =>
        consumedStarts.has(u.start_idx),
      );
      if (!allUsagesConsumed) return true;
      if (phraseVocabularyIds.has(voc.id)) return true;
      return false;
    });
  }, [
    consumedStarts,
    vocabulariesRaw,
    phraseVocabularyIds,
    optimisticPhraseVocabularies,
  ]);
  const resolvedDetailsTitle = resolveGeneratedTextContent({
    rawText:
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "",
    fallbackTitle: reepriseDetails.data?.topic ?? "",
    generatingTitleCopy: translate("text_card_generating_title"),
    isComplete: reepriseDetails.data?.status === "COMPLETE",
    maxWords: reepriseDetails.data?.word_number,
    enforceTitleFirst: true,
  }).displayTitle.trim();
  const isPodcastText = reepriseDetails.data?.text_type === "PODCAST";
  const podcastTranscript = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
      maxWords: reepriseDetails.data?.word_number,
      enforceTitleFirst: true,
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const resolvedDisplayText = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
      maxWords: reepriseDetails.data?.word_number,
      enforceTitleFirst: true,
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const persistedTitleOnlyVocabularyIds = useMemo(
    () =>
      vocabularies
        .filter((voc) => voc.sentence?.trim() === resolvedDetailsTitle)
        .map((voc) => voc.id),
    [resolvedDetailsTitle, vocabularies],
  );
  const selectedVocabularyWords = useMemo(
    () =>
      vocabularies
        .filter(
          (voc) =>
            selectedVocabularyIds.includes(voc.id) &&
            !titleOnlyVocabularyIds.includes(voc.id) &&
            !persistedTitleOnlyVocabularyIds.includes(voc.id),
        )
        .map((voc) => voc.word),
    [
      vocabularies,
      selectedVocabularyIds,
      titleOnlyVocabularyIds,
      persistedTitleOnlyVocabularyIds,
    ],
  );
  const titleVocabularyWords = useMemo(() => {
    const persistedTitleVocabularyWords = vocabularies
      .filter((voc) => persistedTitleOnlyVocabularyIds.includes(voc.id))
      .map((voc) => voc.word);
    const localTitleVocabularyWords = titleOnlyVocabularyIds
      .filter((id) => !persistedTitleOnlyVocabularyIds.includes(id))
      .map((id) => optimisticSelectedWordsById[id] ?? "");
    return [
      ...persistedTitleVocabularyWords,
      ...localTitleVocabularyWords,
    ].filter((word): word is string => Boolean(word?.trim()));
  }, [
    optimisticSelectedWordsById,
    persistedTitleOnlyVocabularyIds,
    titleOnlyVocabularyIds,
    vocabularies,
  ]);
  const selectedVocabularyWordsForGeneration = useMemo(
    () => {
      return resolveSelectedVocabularyWordsForChapterGeneration({
        optimisticWordsById: optimisticSelectedWordsById,
        selectedVocabularyIds,
        vocabularies,
        displayText: resolvedDisplayText,
        generatedTextId: Number(generatedTextId),
        chapterCounter: chapter,
      });
    },
    [
      chapter,
      generatedTextId,
      optimisticSelectedWordsById,
      resolvedDisplayText,
      selectedVocabularyIds,
      vocabularies,
    ],
  );
  const importMetadata = reepriseDetails.data?.image_import_metadata ?? null;
  const imageImportMetadata =
    importMetadata?.source === "image" ? importMetadata : null;
  const articleImportMetadata =
    importMetadata?.source === "url" ? importMetadata : null;
  const isImportedReadingText =
    imageImportMetadata !== null || articleImportMetadata !== null;
  const showInlineContinuationPreview =
    chapter === undefined &&
    reepriseDetails.data?.status === "COMPLETE" &&
    !isImportedReadingText;
  const inlineContinuationErrorMessage = inlineContinuationMutation.isError
    ? translate("generated_text_inline_continue_error")
    : null;
  const activeHighlightWords = resolveActiveTextHighlightWords({
    selectedVocabularyWords,
    titleVocabularyWords,
    chapterGeneratedWords:
      chapter !== undefined &&
      chapterDetails.data &&
      chapterDetails.data.status !== "NON_EXISTENT"
        ? chapterDetails.data.generated_words
        : undefined,
  });
  const imageImportStatusCard =
    imageImportMetadata && chapter === undefined ? (
      <ImageImportStatusCard
        metadata={imageImportMetadata}
        status={reepriseDetails.data?.status}
      />
    ) : null;
  const sourceMetadataCard =
    articleImportMetadata && chapter === undefined ? (
      <ArticleImportSourceCard metadata={articleImportMetadata} />
    ) : null;
  const hasSameIds = (left: number[], right: number[]) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);
  const vocabularyToggleState = showVocabularyColumn ? "open" : "closed";

  useEffect(() => {
    const availableVocabularyIds = vocabularies.map((entry) => entry.id);
    const currentSelectedIds = selectedVocabularyIdsRef.current;
    const currentDeselectedIds = deselectedVocabularyIdsRef.current;
    const preselectedIds =
      resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText({
        origin: reepriseDetails.data?.origin,
        struggleWordIds: [],
        availableIds: availableVocabularyIds,
        selectedIds: currentSelectedIds,
        deselectedIds: currentDeselectedIds,
      });
    const prunedSelection = pruneVocabularySelectionToExistingIds({
      selectedIds: preselectedIds,
      deselectedIds: currentDeselectedIds,
      availableIds: availableVocabularyIds,
    });
    setSelectedVocabularyIds((prev) => {
      const next = prunedSelection.selectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
    setDeselectedVocabularyIds((prev) => {
      const next = prunedSelection.deselectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
  }, [
    reepriseDetails.data?.origin,
    vocabularies,
  ]);

  const continueTextInlineFromSelection = async () => {
    if (inlineContinuationMutation.isPending) return;
    const words = selectedVocabularyWordsForGeneration;
    resetStreamingTextIntegrator({
      textId: Number(generatedTextId),
    });
    try {
      await inlineContinuationMutation.mutateAsync({ words });
    } catch {
      resetStreamingTextIntegrator({ textId: Number(generatedTextId) });
    }
  };

  if (reepriseDetails.data?.status === "FAILED") {
    const customFailureBody = imageImportMetadata
      ? resolveImageImportFailureMessage(
          imageImportMetadata,
          "chatgpt_output" in reepriseDetails.data
            ? reepriseDetails.data.chatgpt_output ?? ""
            : "",
        )
      : undefined;
    return (
      <FailedGeneratedTextScreen
        customBody={customFailureBody}
        generatedTextId={Number(generatedTextId)}
        onRefresh={() => {
          void reepriseDetails.refetch();
        }}
        refreshLabel={
          imageImportMetadata ? translate("image_import_failed_retry") : undefined
        }
      />
    );
  }

  return (
    <>
      <div
        ref={topRef}
        className="bg-neutral-50 px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-left lg:h-full lg:pb-0"
      >
        <div
          data-testid="generated-text-details-layout"
          className="transition-[gap] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:flex lg:h-full lg:justify-center lg:gap-6 xl:gap-8"
        >
          <div
            data-testid="generated-text-reading-pane-shell"
            data-reading-layout={showVocabularyColumn ? "split" : "centered"}
            className={
              showVocabularyColumn
                ? "relative mb-9 rounded-lg transition-[max-width,padding,margin,transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:min-h-full lg:w-full lg:max-w-[56rem] lg:translate-x-0 lg:opacity-100 lg:p-4 xl:max-w-[60rem]"
                : "relative mb-9 rounded-lg transition-[max-width,padding,margin,transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:min-h-full lg:w-full lg:max-w-[56rem] lg:translate-x-0 lg:opacity-100 lg:p-4 xl:max-w-[60rem]"
            }
          >
            <button
              type="button"
              data-testid="generated-text-vocabulary-toggle"
              data-vocabulary-state={vocabularyToggleState}
              aria-label={
                showVocabularyColumn
                  ? translate("hide_vocabulary_column")
                  : translate("show_vocabulary_column")
              }
              title={
                showVocabularyColumn
                  ? translate("hide_vocabulary_column")
                  : translate("show_vocabulary_column")
              }
              className="absolute right-0 top-5 z-20 hidden h-10 w-10 translate-x-1/2 items-center justify-center rounded-full border border-slate-200/90 bg-neutral-50/95 text-slate-500 transition-[transform,color,background-color,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-slate-300 hover:bg-white hover:text-slate-700 lg:flex"
              onClick={() => setShowVocabularyColumn((prev) => !prev)}
            >
              <ChevronRightIcon
                className={
                  "h-5 w-5 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] " +
                  (showVocabularyColumn ? "rotate-0" : "rotate-180")
                }
              />
            </button>
            <ReadingAudioIntegration
              audioSrc={audioSrc}
              chapterNumber={chapter}
              generatedTextId={Number(generatedTextId)}
              isPodcastText={isPodcastText}
              isSpeechPending={createSpeechMutation.isPending}
              podcastTranscript={podcastTranscript}
              requestSpeech={() => {
                createSpeechMutation.mutate({
                  id: Number(generatedTextId),
                  chapter_number: chapter,
                });
              }}
            >
              {({ audioTrigger, playbackStateLayer }) => (
                <div className="relative rounded-2xl">
                  <GeneratedTextDetailsTextPane
                    generatedTextId={Number(generatedTextId)}
                    chapterNumber={chapter}
                    onVocabularyCreate={onVocabularyCreate}
                    highlightWords={activeHighlightWords}
                    focusRequest={focusRequest}
                    metadataSecondary={
                      imageImportStatusCard || sourceMetadataCard ? (
                        <>
                          {imageImportStatusCard}
                          {sourceMetadataCard}
                        </>
                      ) : undefined
                    }
                    readingToolbarAccessory={audioTrigger}
                    readingToolbarSecondary={playbackStateLayer}
                    isInlineContinuationStreaming={inlineContinuationMutation.isPending}
                  />
                  <CreateVocabularyTextInlinePreview
                    isVisible={showInlineContinuationPreview}
                    isLoading={inlineContinuationMutation.isPending}
                    errorMessage={inlineContinuationErrorMessage}
                    onContinue={() => {
                      void continueTextInlineFromSelection();
                    }}
                  />
                </div>
              )}
            </ReadingAudioIntegration>
          </div>
          <div
            ref={vocabularySectionRef}
            className={
              "relative overflow-hidden rounded-lg bg-white-300 transition-[width,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] side-nav-hover-scrollbar " +
              (showVocabularyColumn
                ? "lg:h-full lg:w-[22rem] lg:translate-x-0 lg:opacity-100 xl:w-[28rem]"
                : "max-lg:hidden lg:h-full lg:w-0 lg:translate-x-3 lg:opacity-0 lg:pointer-events-none")
            }
            data-testid="generated-text-vocabulary-section"
            data-vocabulary-state={vocabularyToggleState}
          >
            <div
              className={
                "lg:h-full overflow-y-auto side-nav-hover-scrollbar transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] " +
                (showVocabularyColumn
                  ? "translate-x-0 opacity-100"
                  : "translate-x-4 opacity-0")
              }
            >
              <h2 className="text-l bg-neutral-50 lg:pt-5 font-semibold lg:mb-1 mb-4 lg:sticky top-0 z-10 flex items-center justify-between">
                <span>{translate("vocabulary")}</span>
              </h2>
            {vocabularies.length === 0 && (
              <div className="flex bg-slate-100 mb-4 p-3 rounded-md mt-4">
                <p>
                  <LightBulbIcon className="w-6 h-6 mr-1" />
                </p>
                {translate("translations_click_on_a_word_to_translate_it")}
              </div>
            )}
            <ul className="p-0 m-0 w-full z-20 lg:pt-1 pb-24 lg:pb-0">
              {vocabularies.map((arg) => (
                <WordCard
                  key={arg.id}
                  vocabulary={arg}
                  isSelected={selectedVocabularyIds.includes(arg.id)}
                  addOrRemoveVocabulary={() => {
                    if (selectedVocabularyIds.includes(arg.id)) {
                      setSelectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                      setDeselectedVocabularyIds((prev) => [
                        ...prev.filter((entry) => entry !== arg.id),
                        arg.id,
                      ]);
                    } else {
                      setSelectedVocabularyIds((prev) =>
                        prev.includes(arg.id) ? prev : [...prev, arg.id],
                      );
                      setDeselectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                    }
                  }}
                  onJump={() => {
                    setFocusRequest({
                      word: arg.word,
                      sentence: arg.sentence ?? null,
                      token: Date.now(),
                    });
                  }}
                  textLevel={reepriseDetails.data?.level}
                />
              ))}
              {paginatedVocabularyList.hasNextPage && (
                <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
                  <button
                    className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => {
                      paginatedVocabularyList.fetchNextPage();
                    }}
                  >
                    Load more
                  </button>
                </li>
              )}
            </ul>
            </div>
          </div>
        </div>
      </div>
      {isLanguageHelperOpen && (
        <div
          data-testid="language-helper-overlay"
          className="fixed inset-0 z-50 flex items-start justify-end bg-slate-950/20 px-4 pt-20 pb-4 md:px-8"
          onClick={() => setIsLanguageHelperOpen(false)}
        >
          <div
            className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                data-testid="language-helper-overlay-close"
                aria-label={translate("text_card_close_prompt")}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                onClick={() => setIsLanguageHelperOpen(false)}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <LanguageHelperPanel
              generatedTextId={Number(generatedTextId)}
              chapterNumber={chapter}
              wordLanguage={
                languageSpace !== "ALL"
                  ? languageSpace
                  : reepriseDetails.data?.language
              }
              contextText={
                chapter !== undefined
                  ? chapterDetails.data &&
                    "content" in chapterDetails.data &&
                    chapterDetails.data.status === "COMPLETE"
                    ? chapterDetails.data.content
                    : undefined
                  : reepriseDetails.data &&
                      "chatgpt_output" in reepriseDetails.data
                    ? reepriseDetails.data.chatgpt_output
                    : undefined
              }
              recentVocabulary={recentVocabulary}
            />
          </div>
        </div>
      )}
    </>
  );
}

function ReadingAudioIntegration(props: {
  audioSrc: string;
  chapterNumber: number | undefined;
  generatedTextId: number;
  isPodcastText: boolean;
  isSpeechPending: boolean;
  podcastTranscript: string;
  requestSpeech: () => void;
  children: (slots: {
    audioTrigger: ReactNode;
    playbackStateLayer: ReactNode;
  }) => ReactNode;
}) {
  const { translate } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioProgressRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isAudioScrubbing, setIsAudioScrubbing] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const [isPlaybackStateLayerOpen, setIsPlaybackStateLayerOpen] = useState(false);
  const [showPodcastTranscript, setShowPodcastTranscript] = useState(false);
  const showAudioLoading = isAudioLoading || props.isSpeechPending;

  useEffect(() => {
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
    setIsPlaying(false);
    setIsAudioLoading(false);
    setIsAudioScrubbing(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioPlaybackRate(1);
    setHasStartedPlayback(false);
    setIsPlaybackStateLayerOpen(false);
    setShowPodcastTranscript(false);
  }, [props.audioSrc, props.chapterNumber, props.generatedTextId]);

  const seekAudioByClientX = (clientX: number) => {
    const progressEl = audioProgressRef.current;
    const audioEl = audioRef.current;
    if (!progressEl || !audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration) ? audioEl.duration : 0;
    if (duration <= 0) {
      return;
    }
    const bounds = progressEl.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
  };

  useEffect(() => {
    if (!isAudioScrubbing) {
      return;
    }
    const onMouseMove = (event: MouseEvent) => {
      seekAudioByClientX(event.clientX);
    };
    const onMouseUp = () => {
      setIsAudioScrubbing(false);
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      seekAudioByClientX(touch.clientX);
    };
    const onTouchEnd = () => {
      setIsAudioScrubbing(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isAudioScrubbing]);

  const togglePlayback = () => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    setIsPlaybackStateLayerOpen(true);
    if (isPlaying) {
      audioEl.pause();
      return;
    }
    props.requestSpeech();
    setIsAudioLoading(true);
    void audioEl.play().catch((error) => {
      console.error("Audio play error", error);
      setIsAudioLoading(false);
    });
  };

  const cyclePlaybackRate = () => {
    const nextRate = resolveNextPlaybackRate(audioPlaybackRate);
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.playbackRate = nextRate;
    }
    setAudioPlaybackRate(nextRate);
    setIsPlaybackStateLayerOpen(true);
  };

  const seekByDeltaSeconds = (deltaSeconds: number) => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration)
      ? audioEl.duration
      : audioDuration;
    const nextTime = resolveSeekTime({
      currentTime: audioEl.currentTime,
      deltaSeconds,
      duration,
    });
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
    setHasStartedPlayback(true);
    setIsPlaybackStateLayerOpen(true);
  };

  const progressPercentage =
    audioDuration > 0
      ? Math.min(100, (audioCurrentTime / audioDuration) * 100)
      : 0;
  const shouldShowPlaybackStateLayer =
    hasStartedPlayback && (isPlaying || isPlaybackStateLayerOpen || isAudioScrubbing);
  const hasPlayableDuration = audioDuration > 0;

  return (
    <>
      {props.children({
        audioTrigger: (
          <AudioTrigger
            duration={hasPlayableDuration ? formatAudioClock(audioDuration) : null}
            isLoading={showAudioLoading}
            isPlaying={isPlaying}
            label={translate("audio_player_title")}
            onActivateLayer={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onTogglePlayback={togglePlayback}
          />
        ),
        playbackStateLayer: (
          <PlaybackStateLayer
            isVisible={shouldShowPlaybackStateLayer}
            onBlurCapture={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              if (
                nextTarget &&
                event.currentTarget.contains(nextTarget)
              ) {
                return;
              }
              if (!isPlaying && !isAudioScrubbing) {
                setIsPlaybackStateLayerOpen(false);
              }
            }}
            onFocusCapture={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onMouseEnter={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (!isPlaying && !isAudioScrubbing) {
                setIsPlaybackStateLayerOpen(false);
              }
            }}
          >
            <PlaybackControls
              audioCurrentTime={audioCurrentTime}
              audioDuration={audioDuration}
              audioPlaybackRate={audioPlaybackRate}
              isPlaying={isPlaying}
              onCyclePlaybackRate={cyclePlaybackRate}
              onSeekBackward={() => seekByDeltaSeconds(-10)}
              onSeekForward={() => seekByDeltaSeconds(10)}
              onStartScrub={(clientX) => {
                seekAudioByClientX(clientX);
                setIsAudioScrubbing(true);
                setIsPlaybackStateLayerOpen(true);
              }}
              onTogglePlayback={togglePlayback}
              progressPercentage={progressPercentage}
              progressRef={audioProgressRef}
              translate={translate}
            />
            {props.isPodcastText && (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs text-slate-600 underline underline-offset-2 hover:text-slate-800"
                  onClick={() => {
                    setShowPodcastTranscript((prev) => !prev);
                    setIsPlaybackStateLayerOpen(true);
                  }}
                >
                  {showPodcastTranscript
                    ? translate("podcast_details_hide_transcript")
                    : translate("podcast_details_show_transcript")}
                </button>
                {showPodcastTranscript && (
                  <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                    <p className="text-sm font-ABee-zee leading-tight whitespace-pre-line text-slate-700">
                      {props.podcastTranscript || translate("podcast_details_transcript_pending")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </PlaybackStateLayer>
        ),
      })}
      <audio
        key={props.audioSrc}
        ref={audioRef}
        src={props.audioSrc}
        preload="none"
        playsInline
        className="hidden"
        onPlaying={() => {
          setIsPlaying(true);
          setIsAudioLoading(false);
          setHasStartedPlayback(true);
          setIsPlaybackStateLayerOpen(true);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onWaiting={() => setIsAudioLoading(true)}
        onStalled={() => setIsAudioLoading(true)}
        onError={() => setIsAudioLoading(false)}
        onLoadedMetadata={(event) => {
          setAudioDuration(event.currentTarget.duration || 0);
          setAudioPlaybackRate(event.currentTarget.playbackRate || 1);
        }}
        onTimeUpdate={(event) => {
          setAudioCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onEnded={(event) => {
          setIsPlaying(false);
          setAudioCurrentTime(event.currentTarget.duration || 0);
          setIsPlaybackStateLayerOpen(false);
        }}
      />
    </>
  );
}

function AudioTrigger(props: {
  label: string;
  duration: string | null;
  isLoading: boolean;
  isPlaying: boolean;
  onTogglePlayback: () => void;
  onActivateLayer: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onTogglePlayback}
      onMouseEnter={props.onActivateLayer}
      onFocus={props.onActivateLayer}
      className="group inline-flex min-h-11 items-center gap-1.5 text-slate-500 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
      aria-label={props.label}
      data-testid="generated-text-audio-trigger"
    >
      <span
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center"
        data-testid="generated-text-audio-trigger-icon"
      >
        {props.isLoading ? (
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"
          />
        ) : props.isPlaying ? (
          <PauseIcon className="h-4 w-4" />
        ) : (
          <PlayIcon className="h-4 w-4" />
        )}
      </span>
      <span className="text-sm font-medium">{props.label}</span>
      {props.duration ? (
        <span className="border-l border-slate-300 pl-2 font-serif text-[11px] tabular-nums text-slate-500">
          {props.duration}
        </span>
      ) : null}
    </button>
  );
}

function PlaybackStateLayer(props: {
  isVisible: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocusCapture: () => void;
  onBlurCapture: (event: FocusEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) {
  return (
    <div
      className={[
        "overflow-hidden transition-[max-height,opacity,transform] duration-200",
        props.isVisible
          ? "max-h-72 opacity-100 translate-y-0"
          : "pointer-events-none max-h-0 -translate-y-1 opacity-0",
      ].join(" ")}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onFocusCapture={props.onFocusCapture}
      onBlurCapture={props.onBlurCapture}
    >
      <div className="pt-2">{props.children}</div>
    </div>
  );
}

function PlaybackControls(props: {
  audioCurrentTime: number;
  audioDuration: number;
  audioPlaybackRate: number;
  isPlaying: boolean;
  progressPercentage: number;
  progressRef: RefObject<HTMLDivElement | null>;
  onTogglePlayback: () => void;
  onSeekBackward: () => void;
  onSeekForward: () => void;
  onCyclePlaybackRate: () => void;
  onStartScrub: (clientX: number) => void;
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  return (
    <div
      className="border-y border-slate-200 py-2 text-slate-700"
      data-testid="generated-text-audio-progress"
    >
      <div className="flex items-center gap-3">
        <span className="w-9 text-right font-serif text-[11px] tabular-nums text-slate-500">
          {formatAudioClock(props.audioCurrentTime)}
        </span>
        <div
          ref={props.progressRef}
          className="relative h-8 flex-1 cursor-pointer touch-none"
          onMouseDown={(event) => {
            props.onStartScrub(event.clientX);
          }}
          onTouchStart={(event) => {
            const touch = event.touches[0];
            if (!touch) {
              return;
            }
            props.onStartScrub(touch.clientX);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              props.onSeekBackward();
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              props.onSeekForward();
            }
          }}
          role="slider"
          tabIndex={0}
          aria-label={props.translate("audio_player_title")}
          aria-valuemin={0}
          aria-valuemax={props.audioDuration || 0}
          aria-valuenow={props.audioCurrentTime}
          data-testid="generated-text-audio-progress-track"
        >
          <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-300" />
          <div
            className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-slate-900 transition-[width] duration-150"
            style={{ width: `${props.progressPercentage}%` }}
          />
          <span
            className="pointer-events-none absolute top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            style={{ left: `${props.progressPercentage}%` }}
            aria-hidden="true"
          >
            <span className="h-4 w-[3px] border-x border-slate-700 bg-neutral-50" />
          </span>
        </div>
        <span className="w-9 font-serif text-[11px] tabular-nums text-slate-500">
          {formatAudioClock(props.audioDuration)}
        </span>
      </div>

      <div className="mt-1 flex items-center justify-between border-t border-slate-200/80 pt-1">
        <div className="flex items-center">
          <button
            type="button"
            onClick={props.onSeekBackward}
            className="min-h-11 min-w-11 px-2 text-xs tabular-nums text-slate-500 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            aria-label={props.translate("audio_player_skip_back")}
          >
            −10
          </button>
          <button
            type="button"
            onClick={props.onTogglePlayback}
            className="flex min-h-11 min-w-11 items-center justify-center border-x border-slate-200 text-slate-800 hover:bg-slate-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            aria-label={
              props.isPlaying
                ? props.translate("audio_player_pause")
                : props.translate("audio_player_play")
            }
          >
            {props.isPlaying ? (
              <PauseIcon className="h-4 w-4" />
            ) : (
              <PlayIcon className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={props.onSeekForward}
            className="min-h-11 min-w-11 px-2 text-xs tabular-nums text-slate-500 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            aria-label={props.translate("audio_player_skip_forward")}
          >
            +10
          </button>
        </div>
        <button
          type="button"
          onClick={props.onCyclePlaybackRate}
          className="min-h-11 px-2 text-xs tabular-nums text-slate-500 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          aria-label={props.translate("audio_player_speed")}
        >
          {`${props.audioPlaybackRate}×`}
        </button>
      </div>
    </div>
  );
}

function FailedGeneratedTextScreen(props: {
  generatedTextId: number;
  onRefresh: () => void;
  customBody?: string;
  refreshLabel?: string;
}) {
  const { translate } = useTranslation();

  return (
    <div
      data-testid="generated-text-failed-screen"
      className="min-h-full bg-stone-100 px-5 py-8 md:px-8 md:py-12"
    >
      <div className="mx-auto max-w-3xl rounded-[28px] border border-stone-300 bg-white">
        <div className="mx-auto max-w-2xl px-6 py-8 md:px-10 md:py-10">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 pb-4">
            <p className="text-sm font-medium text-stone-700">
              {translate("generated_text_failed_badge")}
            </p>
            <p className="text-sm tabular-nums text-stone-500">
              {`#${props.generatedTextId}`}
            </p>
          </div>
          <h1 className="mt-6 max-w-xl text-3xl text-stone-950 md:text-[2rem] md:leading-tight">
            {translate("generated_text_failed_title")}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-7 text-stone-700">
            {props.customBody || translate("generated_text_failed_body")}
          </p>
          <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4 text-sm text-stone-600">
            <p>{translate("generated_text_failed_contact_note")}</p>
          </div>
          <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <button
              type="button"
              data-testid="generated-text-failed-refresh"
              onClick={props.onRefresh}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-stone-900 bg-stone-900 px-6 text-base font-medium text-white transition-colors hover:bg-stone-800"
            >
              {props.refreshLabel || translate("generated_text_failed_refresh")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageImportStatusCard(props: {
  metadata: ImageImportMetadata;
  status: "PENDING" | "COMPLETE" | "FAILED" | undefined;
}) {
  const { translate } = useTranslation();

  if (props.status === "PENDING") {
    return (
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">
          {translate(
            resolveImageImportStageLabelKey(
              props.metadata.processing_stage,
              props.metadata.mode,
            ),
          )}
        </p>
        <p className="mt-1 text-slate-600">
          {translate("image_import_processing_notice")}
        </p>
      </div>
    );
  }

  if (props.status === "COMPLETE" && props.metadata.shortened) {
    return (
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">
          {translate("image_import_shortened_title")}
        </p>
        <p className="mt-1 text-slate-600">
          {props.metadata.shortening_reason?.trim() ||
            translate("image_import_shortened_body")}
        </p>
      </div>
    );
  }

  return null;
}

function ArticleImportSourceCard(props: { metadata: ImageImportMetadata }) {
  const { translate } = useTranslation();
  const sourceUrl = props.metadata.canonical_url || props.metadata.source_url;
  const sourceBits = [
    props.metadata.site_name?.trim(),
    props.metadata.source_language?.trim(),
  ].filter(Boolean);

  if (
    !sourceUrl &&
    !props.metadata.original_title?.trim() &&
    !props.metadata.excerpt?.trim() &&
    sourceBits.length === 0
  ) {
    return null;
  }

  return (
    <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
      <p className="font-semibold text-slate-900">
        {translate("article_import_source_title")}
      </p>
      {props.metadata.original_title?.trim() && (
        <p className="mt-1 text-slate-800">{props.metadata.original_title.trim()}</p>
      )}
      {sourceBits.length > 0 && (
        <p className="mt-1 text-slate-600">{sourceBits.join(" · ")}</p>
      )}
      {props.metadata.excerpt?.trim() && (
        <p className="mt-2 text-slate-600">{props.metadata.excerpt.trim()}</p>
      )}
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 block break-all text-slate-700 underline"
        >
          {sourceUrl}
        </a>
      )}
    </div>
  );
}


function formatAudioClock(rawSeconds: number) {
  if (!Number.isFinite(rawSeconds) || rawSeconds < 0) {
    return "0:00";
  }
  const totalSeconds = Math.floor(rawSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
